import React, { createContext, useContext, ReactNode, useReducer, useMemo } from "react";
import { mapReducer, initialMapState, MapState, MapAction } from "../reducer/mapReducer";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";

// Create separate contexts for different parts of the state
interface MapStateContextType {
  state: MapState;
}

interface MapDispatchContextType {
  dispatch: React.Dispatch<MapAction>;
}

// Create the contexts
const MapStateContext = createContext<MapStateContextType | undefined>(undefined);
const MapDispatchContext = createContext<MapDispatchContextType | undefined>(undefined);

// Create a provider component
interface MapProviderProps {
  children: ReactNode;
}

export const MapProvider: React.FC<MapProviderProps> = ({ children }) => {
  const { ssrNearbyLocations } = useSelector(
    ({ propertyPageData }: AppState) => ({
      ssrNearbyLocations: propertyPageData?.nearbyLocations,
    }),
    shallowEqual,
  );

  // Filter out tabs with empty items array (e.g. Hospitals, Police, Shopping Malls with no data)
  const filteredNearbyLocations = ssrNearbyLocations?.filter((tab: any) => tab?.items?.length > 0);

  const ssrInitialState = {
    ...initialMapState,
    ...(filteredNearbyLocations && filteredNearbyLocations.length > 0
      ? { tabs: filteredNearbyLocations, activeTab: filteredNearbyLocations[0] }
      : {}),
  };

  const [state, dispatch] = useReducer(mapReducer, ssrInitialState);

  // Memoize the context values to prevent unnecessary re-renders
  const stateValue = useMemo(() => ({ state }), [state]);
  const dispatchValue = useMemo(() => ({ dispatch }), []);

  return (
    <MapDispatchContext.Provider value={dispatchValue}>
      <MapStateContext.Provider value={stateValue}>{children}</MapStateContext.Provider>
    </MapDispatchContext.Provider>
  );
};

// Create custom hooks to use the map contexts
export const useMapState = (): MapState => {
  const context = useContext(MapStateContext);
  if (context === undefined) {
    throw new Error("useMapState must be used within a MapProvider");
  }
  return context.state;
};

export const useMapDispatch = (): React.Dispatch<MapAction> => {
  const context = useContext(MapDispatchContext);
  if (context === undefined) {
    throw new Error("useMapDispatch must be used within a MapProvider");
  }
  return context.dispatch;
};

// For backward compatibility
export const useMapContext = () => {
  const state = useMapState();
  const dispatch = useMapDispatch();
  return { state, dispatch };
};
